import { useMemo, useState } from 'react';
import {
  EDUCATION_LEVEL_LABELS,
  ICP_GROUPS,
  JEV_ENGLISH_LABELS,
  JEV_EVIDENCE_LABELS,
  compareIcpRanks,
  icpAliasesOf,
  icpPartTextOf,
  icpTextKeyOf,
  type EducationLevel,
  type IcpFact,
  type IcpGroup,
  type IcpItem,
  type IcpItemValue,
  type IcpJevItem,
  type IcpMark,
  type IcpRank,
  type JevEnglish,
  type JevEvidence,
  type RankTab,
  type RankingRow,
} from '@nest-recruit/shared';
import { EVIDENCE_GLYPHS, EVIDENCE_TONES } from './components/JevMatch';
import type { RankedListRow } from './ranking-view';

// ADR 0062 on RNK-01, in the browser: the ideal candidate profile is the main ranking, so its order, its tabs and its counts are
// worked out here from the rows and the ratings on screen (the shared `icpRanksOf`), the "Moved" column, and the small pure helpers
// the card, the form and the table share. A changed rating re-orders the table with no request (power sorting costs nothing).

// ---- The order ----

/**
 * The rows with the profile's own match, stars, tab, covers and key laid over what the server stored (PLAN §2.7): the stored
 * ranking catches up about 3 seconds after a rating changes, so the tabs and their counts are the browser's meanwhile.
 */
export function withProfileRanks(rows: readonly RankingRow[], ranks: ReadonlyMap<string, IcpRank>): readonly RankingRow[] {
  return rows.map((row) => {
    const rank = ranks.get(row.applicationId);
    if (!rank) return row;
    return { ...row, score: rank.match, stars: rank.stars, tab: rank.tab, coverage: rank.covers, missingMustHaves: rank.missingMusts, rankKey: rank.rankKey, isPending: rank.isPending };
  });
}

/** One tab's rows in the profile's order (every must-have first, then the match; import order breaks a tie), searched by name and numbered from 1. */
export function profileRows(rows: readonly RankingRow[], filter: { readonly tab: RankTab; readonly query: string; readonly direction: 'asc' | 'desc' }): readonly RankedListRow[] {
  const query = filter.query.trim().toLowerCase();
  const sign = filter.direction === 'desc' ? 1 : -1;
  return rows
    .filter((row) => row.tab === filter.tab && (query === '' || row.name.toLowerCase().includes(query)))
    .sort((a, b) => sign * compareIcpRanks(a, b))
    .map((row, index) => ({ ...row, rank: index + 1, focus: null }));
}

/**
 * Jev lines nobody's reading covers yet, or whose words changed since: they count for no one until read ("Not read yet"). English is
 * a per-CV check every reading makes, so it is never stale. Only said once somebody has a reading, so a job nobody has been read
 * for shows no tag on every line.
 */
export function unreadItemIdsOf(items: readonly IcpItem[], rows: readonly RankingRow[]): ReadonlySet<string> {
  const readings = rows.flatMap((row) => (row.profileReading?.status === 'read' ? [row.profileReading] : []));
  if (readings.length === 0) return new Set();
  const stale = new Set<string>();
  for (const item of items) {
    if (item.kind !== 'jev') continue;
    const key = icpTextKeyOf(icpPartTextOf(item));
    if (!readings.some((reading) => reading.lines.some((line) => line.itemId === item.id && line.textKey === key))) stale.add(item.id);
  }
  return stale;
}

/**
 * How many places each person moved since the order before the latest change of the ratings: positive up, negative down,
 * null for no move (or no earlier order to compare with). Only people in both lists are compared, so a tab or a filter
 * that changed the list never invents a move.
 */
export function movedOf(previousIds: readonly string[] | null, currentIds: readonly string[]): ReadonlyMap<string, number | null> {
  const moved = new Map<string, number | null>(currentIds.map((id) => [id, null]));
  if (previousIds === null) return moved;
  const current = new Set(currentIds);
  const previous = new Set(previousIds);
  const before = previousIds.filter((id) => current.has(id));
  const after = currentIds.filter((id) => previous.has(id));
  const placeBefore = new Map(before.map((id, index) => [id, index]));
  after.forEach((id, index) => {
    const delta = (placeBefore.get(id) ?? index) - index;
    moved.set(id, delta === 0 ? null : delta);
  });
  return moved;
}

interface MovedState {
  readonly key: string;
  readonly ids: readonly string[];
  /** The order just before `key` last changed; null until it has. */
  readonly previous: readonly string[] | null;
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const members = new Set(a);
  return b.every((id) => members.has(id));
}

function sameOrder(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/** The next state of the Moved memory, as a pure step. A new key (the effective ratings changed) remembers the order it replaced. */
export function nextMovedState(state: MovedState, key: string, ids: readonly string[]): MovedState {
  if (key !== state.key) return { key, ids, previous: state.ids };
  if (sameOrder(state.ids, ids)) return state;
  // The same ratings with other people on screen (a tab, a filter): nothing to compare with.
  return sameMembers(state.ids, ids) ? { ...state, ids } : { key, ids, previous: null };
}

/**
 * Keeps the order from just before the latest change to the effective ratings — mine, or one that arrived by a push —
 * and says who moved. React's "storing information from previous renders": set while rendering, never in an effect.
 */
export function useMovedOrder(ratingsKey: string, ids: readonly string[]): ReadonlyMap<string, number | null> {
  const [state, setState] = useState<MovedState>({ key: ratingsKey, ids, previous: null });
  const next = nextMovedState(state, ratingsKey, ids);
  if (next !== state) setState(next);
  return useMemo(() => movedOf(next.previous, next.ids), [next]);
}

// ---- The profile's lines ----

export interface ItemGroup {
  readonly group: IcpGroup;
  readonly items: readonly IcpItem[];
}

/** The four groups in the mockup's order, without the empty ones. */
export function groupsOf(items: readonly IcpItem[]): readonly ItemGroup[] {
  return ICP_GROUPS.map((group) => ({ group, items: items.filter((item) => item.group === group) })).filter((entry) => entry.items.length > 0);
}

/** The mockup's header words for each group ("Per item: skills · habits · English · facts"). */
const GROUP_HEADER_WORDS: Readonly<Record<IcpGroup, string>> = { skills: 'skills', habits: 'habits', communication: 'English', facts: 'facts' };

/** The Per item column's header, naming only the groups this profile has. */
export function perItemHeaderOf(items: readonly IcpItem[]): string {
  const words = groupsOf(items).map((entry) => GROUP_HEADER_WORDS[entry.group]);
  return words.length === 0 ? 'Per item' : `Per item: ${words.join(' · ')}`;
}

function yearsText(years: number): string {
  return `${years} ${years === 1 ? 'year' : 'years'}`;
}

function yearsRange(fact: Extract<IcpFact, { type: 'years' }>): string {
  if (fact.maxYears === null) return `${yearsText(fact.minYears)} or more`;
  if (fact.maxYears === fact.minYears) return yearsText(fact.minYears);
  return `${fact.minYears}–${yearsText(fact.maxYears)}`;
}

/** What follows "Counts as met when" on a fact line, written as a sentence ending ("they live in Dhaka"). */
export function factWhenOf(fact: IcpFact): string {
  switch (fact.type) {
    case 'years':
      return `they have ${yearsRange(fact)} of experience`;
    case 'education':
      return `their education is ${EDUCATION_LEVEL_LABELS[fact.minLevel]} or higher`;
    case 'location': {
      const aliases = icpAliasesOf(fact.city, fact.aliases);
      return aliases.length > 0 ? `they live in ${fact.city} (also ${aliases.join(', ')})` : `they live in ${fact.city}`;
    }
    case 'salary':
      return "their expected salary is at or under the job's budget top";
    case 'notice':
      return fact.withinDays === 0 ? 'they can start immediately' : `they can start within ${fact.withinDays} ${fact.withinDays === 1 ? 'day' : 'days'}`;
  }
}

// ---- Per-item glyphs ----

export type GlyphTone = 'shows' | 'unclear' | 'not_shown' | 'unknown';

export interface GlyphView {
  readonly text: string;
  readonly tone: GlyphTone;
  /** What the mark means in words, for the tooltip and for people who can't see colour. */
  readonly label: string;
}

const ENGLISH_GLYPHS: Readonly<Record<JevEnglish, { readonly text: string; readonly tone: GlyphTone }>> = {
  many_mistakes: { text: '✗', tone: 'not_shown' },
  some_mistakes: { text: '~', tone: 'unclear' },
  few_mistakes: { text: '✓', tone: 'shows' },
  clear: { text: '✓✓', tone: 'shows' },
};

const FACT_GLYPHS: Readonly<Record<'met' | 'near' | 'not_met', { readonly text: string; readonly tone: GlyphTone; readonly label: string }>> = {
  met: { text: '✓', tone: 'shows', label: 'Met' },
  near: { text: '~', tone: 'unclear', label: 'Close' },
  not_met: { text: '✗', tone: 'not_shown', label: 'Not met' },
};

/** Marks that say nothing about the person: shown as a dashed box, "unknown, not a no". */
const UNSEEN_LABELS: Readonly<Record<'unknown' | 'hidden' | 'no_budget' | 'remote' | 'not_read_yet', string>> = {
  unknown: 'Not given',
  hidden: 'Hidden from you — not in your order',
  no_budget: 'The job has no budget — not in the order',
  remote: 'Remote job — not in the order',
  not_read_yet: 'Not read yet — not in the order',
};

const JEV_EVIDENCE_MARKS: readonly IcpMark[] = ['strong', 'shown', 'related', 'named', 'none'];
const JEV_ENGLISH_MARKS: readonly IcpMark[] = ['many_mistakes', 'some_mistakes', 'few_mistakes', 'clear'];

function isEvidenceMark(mark: IcpMark): mark is JevEvidence {
  return JEV_EVIDENCE_MARKS.includes(mark);
}

function isEnglishMark(mark: IcpMark): mark is JevEnglish {
  return JEV_ENGLISH_MARKS.includes(mark);
}

/** One item's box in the "Per item" cell: a glyph and a colour, or a dashed dash when the person left it open. */
export function glyphOf(value: Pick<IcpItemValue, 'mark' | 'isExcluded'>): GlyphView {
  const { mark } = value;
  if (isEvidenceMark(mark) && !value.isExcluded) return { text: EVIDENCE_GLYPHS[mark], tone: EVIDENCE_TONES[mark], label: JEV_EVIDENCE_LABELS[mark] };
  if (isEnglishMark(mark) && !value.isExcluded) return { ...ENGLISH_GLYPHS[mark], label: JEV_ENGLISH_LABELS[mark] };
  if (mark === 'met' || mark === 'near' || mark === 'not_met') return value.isExcluded ? { text: '–', tone: 'unknown', label: UNSEEN_LABELS.not_read_yet } : FACT_GLYPHS[mark];
  const unseen = mark === 'hidden' || mark === 'no_budget' || mark === 'remote' || mark === 'not_read_yet' ? mark : 'unknown';
  return { text: '–', tone: 'unknown', label: UNSEEN_LABELS[unseen] };
}

export interface JevFoundLine {
  readonly item: IcpJevItem;
  readonly glyph: GlyphView;
}

export interface JevFoundSummary {
  /** The lines Jev reads from the CV (skills and work habits), each with its box. English and facts are not Jev's findings. */
  readonly lines: readonly JevFoundLine[];
  /** The strongest line Jev found something for; null when every line is "Not shown" or unknown, which is never worded as a no. */
  readonly best: JevFoundLine | null;
}

const EVIDENCE_STRENGTH: readonly IcpMark[] = ['strong', 'shown', 'related', 'named'];

/** The "What Jev found" cell of one person: a box per Jev line, and the strongest of them in words. */
export function jevFoundOf(items: readonly IcpItem[], values: readonly IcpItemValue[]): JevFoundSummary {
  const lines: { readonly line: JevFoundLine; readonly strength: number }[] = [];
  items.forEach((item, index) => {
    const value = values[index];
    if (item.kind !== 'jev' || !value) return;
    lines.push({ line: { item, glyph: glyphOf(value) }, strength: value.isExcluded ? -1 : EVIDENCE_STRENGTH.indexOf(value.mark) });
  });
  const found = lines.filter((entry) => entry.strength >= 0);
  const best = found.reduce<(typeof found)[number] | null>((top, entry) => (top === null || entry.strength < top.strength ? entry : top), null);
  return { lines: lines.map((entry) => entry.line), best: best?.line ?? null };
}

/** The box's tooltip: "Plans delivery routes · rated 5 · Shown in real work". */
export function glyphTitle(item: IcpItem, value: IcpItemValue): string {
  return `${item.text} · rated ${item.rating} · ${glyphOf(value).label}`;
}

// ---- The line under a name ----

const MONTHS_PER_YEAR = 12;
const MONTHS_PER_HALF_YEAR = 6;
const SUMMARY_SEPARATOR = ' · ';

/** "3 yrs", "1.5 yrs", "1 yr", or "under 1 yr" below a year: whole or half years, as the mockup's "3 yrs dispatch…". */
export function experienceText(months: number): string {
  if (months < MONTHS_PER_YEAR) return 'under 1 yr';
  const years = Math.round(months / MONTHS_PER_HALF_YEAR) / 2;
  return `${years} ${years === 1 ? 'yr' : 'yrs'}`;
}

export interface CandidateFacts {
  readonly experienceMonths: number | null;
  readonly education: EducationLevel | null;
  readonly city: string | null;
}

/** "3 yrs · Bachelor's · Chattogram" from what the ranking row already carries; a blank or restricted (null) part is left out, nothing at all when all are. */
export function candidateSummaryOf(facts: CandidateFacts): string | null {
  const city = facts.city?.trim();
  const parts = [facts.experienceMonths === null ? null : experienceText(facts.experienceMonths), facts.education === null ? null : EDUCATION_LEVEL_LABELS[facts.education], city || null];
  const present = parts.filter((part): part is string => part !== null);
  return present.length === 0 ? null : present.join(SUMMARY_SEPARATOR);
}

/** "▲2" / "▼1", or null when the person did not move. */
export function movedText(moved: number | null | undefined): string | null {
  if (moved === null || moved === undefined || moved === 0) return null;
  return moved > 0 ? `▲${moved}` : `▼${-moved}`;
}

/** "Up 2 places" / "Down 1 place" for people who read the number aloud. */
export function movedLabel(moved: number): string {
  const places = Math.abs(moved);
  return `${moved > 0 ? 'Up' : 'Down'} ${places} ${places === 1 ? 'place' : 'places'}`;
}
